import React, { useMemo, useState, useEffect, useCallback, useLayoutEffect } from 'react';
import { BREAKPOINTS } from '../constants.js';
import { getAnchorStyle, parseThemeColor as parseThemeColorWithTheme } from '../utils.js';
import { parseThemeColor as parseThemeColorToVar, getCurrentTheme, onThemeChange } from '../utils/theme-helper.js';
import { getBehavior } from './behaviors/registry.js';
import { VariantReader } from 'aldoo-ra/VariantReader';
import { TailwindParser } from '../utils/TailwindParser.js';
import { resolveNode } from '../utils/responsive.js';

const applyBindingsToNode = (node, bindings, instanceData) => {
    const nodeBindings = bindings.filter(b => b.nodeId === node.id);
    if (nodeBindings.length === 0) return node;

    const newProps = { ...node.props };
    let newAttachedComponents = node.attachedComponents ? [...node.attachedComponents] : [];
    let hasChanges = false;

    nodeBindings.forEach(binding => {
        const val = instanceData[binding.externalKey];
        if (val !== undefined) {
            if (binding.componentId === 'native') {
                newProps[binding.property] = val;
                hasChanges = true;
            } else {
                const compIndex = newAttachedComponents.findIndex(c => c.id === binding.componentId);
                if (compIndex !== -1) {
                    newAttachedComponents[compIndex] = {
                        ...newAttachedComponents[compIndex],
                        props: {
                            ...newAttachedComponents[compIndex].props,
                            [binding.property]: val
                        }
                    };
                    hasChanges = true;
                }
            }
        }
    });

    if (hasChanges) {
        return {
            ...node,
            props: newProps,
            attachedComponents: newAttachedComponents
        };
    }
    return node;
};

const ComponentRenderer = ({
  node,
  children,
  prefabs,
  isLiveMode,
  initialSize,
  activeBreakpoint = 'default',
  useCSSLayout,
  allNodes, // All nodes for ContentSizeFitter calculations
  computedSize,
  // Positioning props (from NodeItem merge)
  wrapperStyle,
  wrapperClassName,
  wrapperHandlers,
  wrapperProps
}) => {
  const { type, props, attachedComponents } = node;
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isActive, setIsActive] = useState(false);

  // Track theme changes
  const [themeVersion, setThemeVersion] = useState(0);
  const [currentTheme, setCurrentTheme] = useState(() => getCurrentTheme());

  // Track ContentSizeFitter dimensions
  const [fittedDimensions, setFittedDimensions] = useState({});

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const theme = getCurrentTheme();
    if (theme) {
      setCurrentTheme(theme);
      setThemeVersion(v => v + 1);
    }
    const cleanup = onThemeChange((newTheme) => {
      setCurrentTheme(newTheme);
      setThemeVersion(v => v + 1);
    });
    return cleanup;
  }, []);

  // Calculate ContentSizeFitter dimensions
  const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

  useIsomorphicLayoutEffect(() => {
    if (typeof document === 'undefined') return;

    // Find ContentSizeFitter component
    let contentSizeFitterComp = null;
    let targetNode = node;
    let targetAllNodes = allNodes;

    // For Prefabs, check root definition
    if (props.prefabId && prefabs) {
      const prefabDef = prefabs.find(p => p.id === props.prefabId);
      if (prefabDef) {
        const rawRootDef = prefabDef.nodes[0];
        let rootDef = resolveNode(rawRootDef, activeBreakpoint);

        // Apply bindings to rootDef for accurate sizing
        const definitionBindings = (rootDef.attachedComponents?.find(c => c.type === 'DataBinding')?.props?.bindings) || [];
        const instanceData = props.data || {};
        rootDef = applyBindingsToNode(rootDef, definitionBindings, instanceData);

        targetNode = rootDef;
        targetAllNodes = prefabDef.nodes;

        // Check root definition first
        contentSizeFitterComp = rootDef.attachedComponents?.find(
          comp => comp.type === 'ContentSizeFitter' && comp.props?.enabled !== false
        );
      }
    }

    // Check instance-level components (can override prefab definition)
    if (!contentSizeFitterComp) {
      contentSizeFitterComp = attachedComponents?.find(
        comp => comp.type === 'ContentSizeFitter' && comp.props?.enabled !== false
      );
    }

    if (!contentSizeFitterComp) {
      if (Object.keys(fittedDimensions).length > 0) setFittedDimensions({});
      return;
    }

    const horizontalFit = contentSizeFitterComp.props?.horizontalFit || 'Off';
    const verticalFit = contentSizeFitterComp.props?.verticalFit || 'Off';

    if (horizontalFit === 'Off' && verticalFit === 'Off') {
      if (Object.keys(fittedDimensions).length > 0) setFittedDimensions({});
      return;
    }

    const def = getBehavior('ContentSizeFitter');
    if (def && def.getStyle) {
      let availableW = 10000; 
      let availableH = 10000;
      
      const el = document.getElementById(node.id);
      if (el && el.offsetParent) {
          const rect = el.offsetParent.getBoundingClientRect();
          availableW = rect.width;
          availableH = rect.height;
      } else {
          const parent = allNodes.find(n => n.id === node.parentId);
          if (parent) {
              const resolvedParent = resolveNode(parent, activeBreakpoint);
              if (resolvedParent.width) availableW = resolvedParent.width;
              if (resolvedParent.height) availableH = resolvedParent.height;
          }
      }

      const calculatedStyle = def.getStyle(
        contentSizeFitterComp.props,
        isLiveMode,
        targetNode,
        targetAllNodes || allNodes,
        parseFloat(availableW),
        parseFloat(availableH)
      );

      const newDimensions = {};
      if (calculatedStyle.width) newDimensions.width = calculatedStyle.width;
      if (calculatedStyle.height) newDimensions.height = calculatedStyle.height;

      const currentW = parseFloat(fittedDimensions.width);
      const currentH = parseFloat(fittedDimensions.height);
      const newW = parseFloat(newDimensions.width);
      const newH = parseFloat(newDimensions.height);

      const valChanged = (oldVal, newVal) => {
          if (Number.isNaN(oldVal) && Number.isNaN(newVal)) return false;
          if (Number.isNaN(oldVal) || Number.isNaN(newVal)) return true;
          return Math.abs(newVal - oldVal) > 0.1;
      };

      if (valChanged(currentW, newW) || valChanged(currentH, newH)) {
        setFittedDimensions(newDimensions);
      }
    }
  });

  // Resolve Text Behavior
  let effectiveProps = { ...props };
  const activeBehaviors = attachedComponents ? attachedComponents.filter(c => c.props?.enabled !== false) : [];
  const textBehavior = activeBehaviors.find(c => c.type === 'Text');

  if (textBehavior && textBehavior.props) {
      effectiveProps = { ...props, ...textBehavior.props };
  }

  // Resolve Variants
  const rawVariants = effectiveProps.variant || '';
  const disabledVariants = effectiveProps.disabledVariants || [];
  const activeVariants = rawVariants.split(' ').filter(v => v && !disabledVariants.includes(v)).join(' ');

  const variantClassString = VariantReader.useVariantClassString({
      componentName: effectiveProps.variantComponent || 'Typography',
      variant: activeVariants
  });

  // Helper: Theme Color Check
  const isThemeColorKey = useCallback((value) => {
    if (!value || typeof value !== 'string') return false;
    return !value.startsWith('#') && !value.startsWith('rgb') && !value.startsWith('hsl');
  }, []);

  // Helper: Theme Color Resolve (Inline)
  const resolveThemeColorToInlineStyle = useCallback((colorKey) => {
    if (!colorKey) return undefined;
    if (colorKey.startsWith('#') || colorKey.startsWith('rgb') || colorKey.startsWith('hsl')) return colorKey;
    if (isLiveMode) return undefined; 
    if (currentTheme) return parseThemeColorWithTheme(currentTheme, colorKey);
    return undefined;
  }, [themeVersion, isLiveMode, currentTheme]);

  // Helper: Variant Color Resolve
  const resolveColorForVariantStyles = useCallback((colorKey) => {
    if (!colorKey) return undefined;
    if (colorKey.startsWith('#') || colorKey.startsWith('rgb') || colorKey.startsWith('hsl')) return colorKey;
    if (isLiveMode) return parseThemeColorToVar(colorKey);
    else if (currentTheme) return parseThemeColorWithTheme(currentTheme, colorKey);
    return undefined;
  }, [themeVersion, isLiveMode, currentTheme]);

  // Helper: Theme Props to Classes
  const convertThemePropsToClasses = useCallback((props) => {
    if (!isLiveMode) return '';
    const classes = [];
    if (props.backgroundColor && isThemeColorKey(props.backgroundColor)) classes.push(`bg-${props.backgroundColor}`);
    if (props.color && isThemeColorKey(props.color)) classes.push(`text-${props.color}`);
    return classes.join(' ');
  }, [isThemeColorKey, isLiveMode]);

  const { instanceClasses, instanceProps } = useMemo(() => {
    const baseClasses = props.className || '';
    const baseProps = props.variantData || {};
    const themeClasses = convertThemePropsToClasses(props);
    const combinedClasses = [baseClasses, themeClasses].filter(Boolean).join(' ');
    return { instanceClasses: combinedClasses, instanceProps: baseProps };
  }, [props.className, props.variantData, props.backgroundColor, props.color, convertThemePropsToClasses]);

  const variantData = useMemo(() => {
      const parsed = TailwindParser.parse(variantClassString);
      return { ...parsed, ...instanceProps };
  }, [variantClassString, instanceProps]);

  const stateStyles = useMemo(() => {
    return TailwindParser.parseStates(variantClassString, resolveColorForVariantStyles);
  }, [variantClassString, resolveColorForVariantStyles]);

  const { variantWrapperStyle, variantContentStyle } = useMemo(() => {
    const mergedStyles = {};
    const activeWidth = BREAKPOINTS[activeBreakpoint] || 0;
    const sortedBps = Object.keys(BREAKPOINTS).sort((a, b) => BREAKPOINTS[a] - BREAKPOINTS[b]);

    sortedBps.forEach(bp => {
        const bpWidth = BREAKPOINTS[bp];
        if (bpWidth <= activeWidth) {
             const stylesForBp = stateStyles[bp];
             if (stylesForBp) {
                 if (stylesForBp.normal) Object.assign(mergedStyles, stylesForBp.normal);
                 if (isLiveMode) {
                    if (isHovered && stylesForBp.hover) Object.assign(mergedStyles, stylesForBp.hover);
                    if (isFocused && stylesForBp.focus) Object.assign(mergedStyles, stylesForBp.focus);
                    if (isActive && stylesForBp.active) Object.assign(mergedStyles, stylesForBp.active);
                 }
             }
        }
    });

    if (instanceProps && Object.keys(instanceProps).length > 0) {
        const instanceStyles = TailwindParser.toStyle(instanceProps, resolveColorForVariantStyles);
        Object.assign(mergedStyles, instanceStyles);
    }

    const wrapperStyle = {};
    const contentStyle = {};

    Object.entries(mergedStyles).forEach(([key, val]) => {
        if (key.startsWith('transition') || key === 'animation') {
            wrapperStyle[key] = val;
            contentStyle[key] = val;
            return;
        }
        if (key === 'textAlign') {
            const alignMap = { 'left': 'flex-start', 'center': 'center', 'right': 'flex-end', 'justify': 'space-between', 'start': 'flex-start', 'end': 'flex-end' };
            wrapperStyle.display = 'flex';
            wrapperStyle.justifyContent = alignMap[val] || 'flex-start';
            contentStyle.textAlign = val;
            return;
        }
        if (key === 'verticalAlign') {
            const alignMap = { 'top': 'flex-start', 'middle': 'center', 'bottom': 'flex-end', 'baseline': 'baseline', 'text-top': 'flex-start', 'text-bottom': 'flex-end', 'sub': 'flex-end', 'super': 'flex-start' };
            wrapperStyle.display = 'flex';
            wrapperStyle.alignItems = alignMap[val] || 'flex-start';
            contentStyle.verticalAlign = val;
            return;
        }
        if (key.startsWith('text') || key.startsWith('font') || key === 'color' || key === 'lineHeight' || key === 'letterSpacing' || key === 'whiteSpace' || key === 'wordBreak' || key === 'overflowWrap') {
            contentStyle[key] = val;
        } else {
            wrapperStyle[key] = val;
        }
    });

    return { variantWrapperStyle: wrapperStyle, variantContentStyle: contentStyle };
  }, [stateStyles, isHovered, isFocused, isActive, isLiveMode, activeBreakpoint, useCSSLayout, instanceProps, resolveColorForVariantStyles]);

  const hoverHandlers = {
    onMouseEnter: () => isLiveMode && setIsHovered(true),
    onMouseLeave: () => isLiveMode && setIsHovered(false),
    onFocus: () => isLiveMode && setIsFocused(true),
    onBlur: () => isLiveMode && setIsFocused(false),
    onMouseDown: () => isLiveMode && setIsActive(true),
    onMouseUp: () => isLiveMode && setIsActive(false),
  };

  const hasWrapperMouseDown = wrapperHandlers?.onMouseDown;
  const hasWrapperDoubleClick = wrapperHandlers?.onDoubleClick;

  const combinedHandlers = {
    ...hoverHandlers,
    ...(hasWrapperMouseDown && {
      onMouseDown: (e) => {
        hoverHandlers.onMouseDown();
        wrapperHandlers.onMouseDown(e);
      }
    }),
    ...(hasWrapperDoubleClick && {
      onDoubleClick: wrapperHandlers.onDoubleClick
    })
  };

  // --- BOX STYLE CALCULATION ---
  const _themeCheck = themeVersion;

  let boxStyle = {
    width: initialSize?.width || '100%',
    height: initialSize?.height || '100%',
    position: 'relative',
    overflow: props.overflow || 'visible',
    lineHeight: 1.4,
    whiteSpace: 'pre-wrap'
  };

  if (props.backgroundColor) {
    if (isThemeColorKey(props.backgroundColor)) {
      if (!isLiveMode) {
        const resolvedColor = resolveThemeColorToInlineStyle(props.backgroundColor);
        if (resolvedColor) {
          boxStyle.backgroundColor = resolvedColor;
        }
      }
    } else {
      boxStyle.backgroundColor = props.backgroundColor;
    }
  }

  if (props.horizontalAlign || props.verticalAlign) {
      boxStyle.display = 'flex';
      if (props.horizontalAlign) {
          boxStyle.justifyContent = props.horizontalAlign === 'center' ? 'center' : props.horizontalAlign === 'right' ? 'flex-end' : 'flex-start';
          boxStyle.textAlign = props.horizontalAlign;
      }
      if (props.verticalAlign) {
          boxStyle.alignItems = props.verticalAlign === 'top' ? 'flex-start' : props.verticalAlign === 'bottom' ? 'flex-end' : 'center';
      } else {
          boxStyle.alignItems = 'center';
      }
  }

  if (props.fontSize && !variantContentStyle.fontSize) boxStyle.fontSize = `${props.fontSize}px`;
  if (props.fontWeight && !variantContentStyle.fontWeight) boxStyle.fontWeight = props.fontWeight;

  if (props.color && !variantContentStyle.color) {
    if (isThemeColorKey(props.color)) {
      if (!isLiveMode) {
        const resolvedColor = resolveThemeColorToInlineStyle(props.color);
        if (resolvedColor) {
          boxStyle.color = resolvedColor;
        }
      }
    } else {
      boxStyle.color = props.color;
    }
  }

  // Check if ContentSizeFitter is active before applying behaviors
  let hasContentSizeFitter = false;

  if (attachedComponents) {
    attachedComponents.forEach(comp => {
      if (comp.props && comp.props.enabled === false) return;

      if (comp.type === 'ContentSizeFitter') {
        hasContentSizeFitter = true;
        return;
      }

      const def = getBehavior(comp.type);
      if (def && def.getStyle) {
        const behaviorStyle = def.getStyle(comp.props, isLiveMode, node, allNodes);
        boxStyle = { ...boxStyle, ...behaviorStyle };
      }
    });
  }

  // --- REMOVED PREFAB SPECIAL RENDERING ---
  // Prefabs now render their children normally from the scene, not from the definition
  let prefabChildren = null;
  let prefabWrapperStyle = {};
  let prefabClassName = '';
  let prefabThemeClasses = '';
  let prefabHasContentSizeFitter = false;

  if (false && props.prefabId) {  // Disabled - prefabs are normal objects now
      const prefabDef = prefabs?.find(p => p.id === props.prefabId);
      if (prefabDef) {
          const rawRootDef = prefabDef.nodes[0];
          let rootDef = resolveNode(rawRootDef, activeBreakpoint);
          const definitionBindings = (rootDef.attachedComponents?.find(c => c.type === 'DataBinding')?.props?.bindings) || [];
          const instanceData = node.props.data || {};
          rootDef = applyBindingsToNode(rootDef, definitionBindings, instanceData);
          
          const overrides = node.props.overrides || {};

          // Recursive helper to render definition nodes
          const renderDefinitionNode = (parentId, parentW, parentH, isParentLayoutManaged) => {
              const childrenNodes = prefabDef.nodes.filter(n => n.parentId === parentId);
              
              return childrenNodes.map(rawChild => {
                  let child = resolveNode(rawChild, activeBreakpoint);
                  child = applyBindingsToNode(child, definitionBindings, instanceData);
                  
                  if (overrides[child.id]) {
                      child = { ...child, props: { ...child.props, ...overrides[child.id].props } };
                  }

                  const isEnabled = child.props?.enabled !== false;
                  if (!isEnabled) return null;

                  let isVisible = true;
                  if (!isLiveMode && child.props?.visibility !== undefined) {
                      if (typeof child.props.visibility === 'boolean') isVisible = child.props.visibility;
                      else if (activeBreakpoint && typeof child.props.visibility === 'object' && child.props.visibility[activeBreakpoint] === false) isVisible = false;
                  }
                  if (!isVisible) return null;

                  const childContentSizeFitterComp = child.attachedComponents?.find(comp => comp.type === 'ContentSizeFitter' && comp.props?.enabled !== false);
                  const childContentSizeFitterConfig = childContentSizeFitterComp ? childContentSizeFitterComp.props : false;
                  
                  // Calculate Style
                  const childStyle = getAnchorStyle(child, parentW, parentH, isParentLayoutManaged, childContentSizeFitterConfig);
                  childStyle.pointerEvents = isLiveMode ? 'auto' : 'none';
                  if (!isVisible && !isLiveMode) {
                      childStyle.opacity = 0.3;
                      childStyle.filter = 'grayscale(100%)';
                  }

                  // Determine Layout Management
                  let childBehaviorDef;
                  if (child.attachedComponents) {
                      for (const ac of child.attachedComponents) {
                          if (ac.props && ac.props.enabled === false) continue;
                          const def = getBehavior(ac.type);
                          if (def && def.isLayoutProvider) {
                              childBehaviorDef = def;
                              break;
                          }
                      }
                  }
                  const isChildLayoutManaged = !!childBehaviorDef;

                  if (useCSSLayout) {
                      ['width', 'height', 'top', 'left', 'right', 'bottom', 'position'].forEach(p => delete childStyle[p]);
                  }

                  return (
                      <ComponentRenderer
                          key={`${child.id}-${child.props?._variantUpdate || 0}`}
                          node={child}
                          prefabs={prefabs}
                          isLiveMode={isLiveMode}
                          activeBreakpoint={activeBreakpoint}
                          useCSSLayout={useCSSLayout}
                          allNodes={prefabDef.nodes}
                          wrapperStyle={childStyle}
                          wrapperClassName={`vbecs-node-${child.id}`}
                          wrapperProps={{ id: child.id }}
                      >
                          {/* Recursion */}
                          {renderDefinitionNode(child.id, child.width, child.height, isChildLayoutManaged)}
                      </ComponentRenderer>
                  );
              });
          };

          // Determine Instance Dimensions
          let instanceW = node.width;
          let instanceH = node.height;
          if (computedSize) {
              instanceW = computedSize.width;
              instanceH = computedSize.height;
          }

          // Start recursion from children of Root
          prefabChildren = renderDefinitionNode(rootDef.id, instanceW, instanceH, false);

          // Apply Prefab Root Styles
          if (rootDef.props.backgroundColor) {
              if (isThemeColorKey(rootDef.props.backgroundColor)) {
                  const resolvedColor = resolveThemeColorToInlineStyle(rootDef.props.backgroundColor);
                  if (resolvedColor) prefabWrapperStyle.backgroundColor = resolvedColor;
              } else {
                  prefabWrapperStyle.backgroundColor = rootDef.props.backgroundColor;
              }
          }
          if (isLiveMode && rootDef.props.backgroundColor && isThemeColorKey(rootDef.props.backgroundColor)) {
              prefabThemeClasses = `bg-${rootDef.props.backgroundColor}`;
          }
          if (rootDef.props.overflow) prefabWrapperStyle.overflow = rootDef.props.overflow;

          if (rootDef.attachedComponents?.some(c => c.type === 'ContentSizeFitter')) {
              prefabHasContentSizeFitter = true;
          }
      }
  }

  // --- MERGE STYLES ---
  if (useCSSLayout) {
      ['width', 'height', 'top', 'left', 'right', 'bottom', 'position'].forEach(p => delete boxStyle[p]);
  }

  let finalBoxStyle = { ...variantWrapperStyle, ...boxStyle, ...prefabWrapperStyle, ...wrapperStyle };

  const hasFixedPosition = variantWrapperStyle.position === 'fixed' || variantWrapperStyle.position === 'sticky';
  if (hasFixedPosition) {
      finalBoxStyle.position = variantWrapperStyle.position;
      if (variantWrapperStyle.top) finalBoxStyle.top = variantWrapperStyle.top;
      if (variantWrapperStyle.left) finalBoxStyle.left = variantWrapperStyle.left;
      if (variantWrapperStyle.right) finalBoxStyle.right = variantWrapperStyle.right;
      if (variantWrapperStyle.bottom) finalBoxStyle.bottom = variantWrapperStyle.bottom;
      if (variantWrapperStyle.zIndex) finalBoxStyle.zIndex = variantWrapperStyle.zIndex;
  }

  let mergedHasContentSizeFitter = hasContentSizeFitter || prefabHasContentSizeFitter;
  if (mergedHasContentSizeFitter) {
    finalBoxStyle = { ...finalBoxStyle, ...fittedDimensions };
  }

  Object.keys(finalBoxStyle).forEach(key => {
      if (finalBoxStyle[key] === undefined) delete finalBoxStyle[key];
      if (key === 'backgroundColor' && finalBoxStyle[key] === 'transparent') delete finalBoxStyle[key];
  });

  const finalClassName = wrapperClassName
    ? `${wrapperClassName} ${instanceClasses} ${isLiveMode ? '' : (props.backgroundColor === 'transparent' ? 'border border-dashed border-slate-300' : '')} ${prefabThemeClasses} ${isLiveMode ? 'pointer-events-auto' : ''}`.trim()
    : `${instanceClasses} ${isLiveMode ? '' : (props.backgroundColor === 'transparent' ? 'border border-dashed border-slate-300' : '')} ${prefabThemeClasses} ${isLiveMode ? 'pointer-events-auto' : ''}`.trim();

  const finalContentStyle = { ...variantContentStyle };
  if (props.color && !variantContentStyle.color && !isThemeColorKey(props.color)) {
      finalContentStyle.color = props.color;
  }

  return (
    <div
      {...wrapperProps}
      className={finalClassName}
      style={finalBoxStyle}
      {...(!isLiveMode && variantData ? { 'data-variant-data': JSON.stringify(variantData) } : {})}
      {...combinedHandlers}
      tabIndex={isLiveMode && (stateStyles.focus || stateStyles.active) ? 0 : undefined}
    >
      {/* Attached Components */}
      {attachedComponents && attachedComponents.map(comp => {
          if (comp.props && comp.props.enabled === false) return null;
          const def = getBehavior(comp.type);
          if (def && def.renderContent) {
              return <React.Fragment key={comp.id}>
                  {def.renderContent(comp.props, isLiveMode || false, '', finalContentStyle)}
              </React.Fragment>
          }
          return null;
      })}
      
      {/* Prefab Children */}
      {prefabChildren}
      
      {/* Prefab Indicator */}
      {props.prefabId && !isLiveMode && (
          <div className="absolute top-0 right-0 bg-blue-600 text-[9px] text-white px-1 pointer-events-none opacity-50 z-50">
              PREFAB
          </div>
      )}

      {/* Standard Children */}
      {children}
    </div>
  );
};

export default ComponentRenderer;
